<template>
  <div class="activity">
    <div class="activity-top">
      <DActivityAvatar :activity="activity" />

      <div class="activity-title">
        <DActivityAuthor :activity="activity" />

        <span>{{ activityAction }}</span>

        <a
          v-if="activityLink.url"
          class="resource-link"
          :href="activityLink.url"
          :title="activityLink.title"
          @click.stop
        >
          {{ activityLink.text }}
        </a>

        <DActivityTimestamp
          :activity="activity"
          class="visible-until-sm"
        />
      </div>

      <DActivityTimestamp
        :activity="activity"
        class="hidden-until-md"
      />

      <DActivityActions
        v-if="isUserSignedIn && !isRepost"
        :activity="activity"
        @activity-removed="activityRemoved"
      />
    </div>

    <DTextWithLinks
      :content="activity.source.content"
      :display-multiline="true"
      :display-typed-links="true"
      :mentions="activity.source.mentions"
    />

    <DActivityBook
      v-if="activityBook"
      :book="activityBook"
      :book-url="bookUrl"
      class="space-top-2em"
      :has-video-review="!!activity.source.video_url"
      :is-external="!activity.source.book"
      :is-user-signed-in="isUserSignedIn"
      :verdict="activity.source.verdict"
    />

    <DPostImage
      v-if="activity.source.type === 'ImagePost'"
      :activity="activity"
      class="space-top-2em"
    />

    <DPostCompareImages
      v-if="activity.source.type === 'CompareImagesPost'"
      :activity="activity"
      :can-vote="isUserSignedIn"
      class="space-top-2em"
    />

    <DPostMultipleChoice
      v-if="activity.source.type === 'MultipleChoicePost'"
      :activity="activity"
      :can-vote="isUserSignedIn"
      class="space-top-2em"
    />
  </div>
</template>

<script>
import DTextWithLinks from '@d-components/d-text-with-links/d-text-with-links.vue';
import URLS from '@d-shared/urls/urls';

import activityMixin from '../shared/activity-mixin';
import DPostCompareImages from './d-post-compare-images/d-post-compare-images.vue';
import DPostImage from './d-post-image/d-post-image.vue';
import DPostMultipleChoice from './d-post-multiple-choice/d-post-multiple-choice.vue';

export default {
  components: {
    DPostCompareImages,
    DPostImage,
    DPostMultipleChoice,
    DTextWithLinks,
  },
  mixins: [
    activityMixin,
  ],
  data: () => ({
    activityAction: null,
    activityBook: null,
    activityLink: {
      text: null,
      title: null,
      url: null,
    },
    bookUrl: null,
  }),
  mounted() {
    this.setActivityAction();
    this.setActivityLink();
    this.setActivityBook();
    this.setBookUrl();
  },
  methods: {
    setActivityAction() {
      // Currently reading
      if (this.activity.source.type === 'CurrentlyReadingPost') {
        if (this.activity.is_owner || this.activity.source.is_owner) {
          this.$set(this, 'activityAction', 'are reading a book');
          return;
        } else {
          this.$set(this, 'activityAction', 'is reading a book');
          return;
        }
      }

      // Recommendation
      if (this.activity.source.type === 'RecommendationPost') {
        this.$set(this, 'activityAction', 'recommended a book');
        return;
      }

      // Update on book
      if (!!this.activity.source.book &&
        (this.activity.source.type === 'Post' || this.activity.source.type === 'ImagePost')) {
        this.$set(this, 'activityAction', 'shared an update on');
        return;
      }

      // Question
      if (this.activity.source.type === 'CompareImagesPost' ||
        this.activity.source.type === 'MultipleChoicePost') {

        if (!!this.activity.source.book) {
          this.$set(this, 'activityAction', 'asked a question on');
          return;
        }

        this.$set(this, 'activityAction', 'asked');
        return;
      }

      this.$set(this, 'activityAction', 'posted');
    },

    setActivityBook() {
      if (this.activity.source.type !== 'CurrentlyReadingPost' &&
        this.activity.source.type !== 'RecommendationPost') {
        return;
      }

      const activityBook = (this.activity.source.book || this.activity.source.external_book);
      if (activityBook) {
        this.$set(this, 'activityBook', activityBook);
        return;
      }

      if (!this.activity.source.external_book_url) {
        return;
      }

      const externalBookInfo = {
        cover: {
          small: {
            url: null,
          },
        },
        covers: {
          small: null,
        },
        title: this.activity.source.external_book_url,
        url: this.activity.source.external_book_url,
      };

      this.$set(this, 'activityBook', externalBookInfo);
    },

    setActivityLink() {
      if (this.activity.source.type === 'CurrentlyReadingPost' ||
        this.activity.source.type === 'RecommendationPost') {
        this.$set(this.activityLink, 'text', null);
        this.$set(this.activityLink, 'title', null);
        this.$set(this.activityLink, 'url', null);
        return;
      }

      if (!!this.activity.source.book) {
        this.$set(this.activityLink, 'text', this.activity.source.book.title);
        this.$set(this.activityLink, 'title', this.activity.source.book.title);
        this.$set(this.activityLink, 'url', this.activity.source.book.url);
        return;
      }

      if (!!this.activity.source.external_book) {
        this.$set(this.activityLink, 'text', this.activity.source.external_book.title);
        this.$set(this.activityLink, 'title', this.activity.source.external_book.title);
        this.$set(this.activityLink, 'url', this.activity.source.external_book.url);
        return;
      }

      this.$set(this.activityLink, 'title', 'Go to post');
      this.$set(this.activityLink, 'url', this.activity.url);

      if (this.activity.source.type === 'CompareImagesPost' ||
        this.activity.source.type === 'MultipleChoicePost') {
        this.$set(this.activityLink, 'text', 'a question');
        return;
      }

      this.$set(this.activityLink, 'text', 'an update');
    },

    setBookUrl() {
      if (!this.activity.source.external_book) {
        return;
      }

      this.$set(
        this,
        'bookUrl',
        URLS.app.bookUserReviews.replace('<book-slug>', this.activity.source.external_book.slug),
      );
    },
  },
};
</script>
